import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';

const CATEGORIES = [
  { value: 'urns', label: 'Urns' },
  { value: 'pouches', label: 'Pouches' },
  { value: 'charms', label: 'Laser Charms' },
  { value: 'clay', label: 'Clay' },
  { value: 'keepsake_supplies', label: 'Keepsake Supplies' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'jewelry_supplies', label: 'Jewelry Supplies' },
  { value: 'bags', label: 'Bags' },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'other', label: 'Other' }
];

const UNIT_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'bag', label: 'Bags' },
  { value: 'box', label: 'Boxes' },
  { value: 'litre', label: 'Litres' }
];

const STATUS_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  warning: 'bg-amber-100 text-amber-800 border-amber-300',
  good: 'bg-green-100 text-green-800 border-green-300',
  unknown: 'bg-gray-100 text-gray-600 border-gray-300'
};

const STATUS_DOT = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  good: 'bg-green-500',
  unknown: 'bg-gray-400'
};

// ============================================================================
// OVERVIEW TAB
// ============================================================================
const OverviewTab = ({ overview, loading, reorderSuggestions, onRefresh }) => {
  if (loading) return <div className="text-center py-12 text-gray-500">Loading inventory...</div>;

  const categories = overview?.categories || {};
  const alerts = overview?.alerts || [];
  const flags = overview?.flags || [];

  const resolveFlag = async (flagId) => {
    try {
      await apiClient.post(`/inventory/flags/${flagId}/resolve`);
      showToast('Flag resolved', 'success');
      onRefresh && onRefresh();
    } catch (e) { showToast('Failed to resolve flag', 'error'); }
  };

  // Separate items that need ordering NOW vs warning
  const orderNow = alerts.filter(a => a.status === 'critical');
  const orderSoon = alerts.filter(a => a.status === 'warning');

  return (
    <div className="space-y-5">
      {/* STAFF FLAGS — manual attention needed */}
      {flags.length > 0 && (
        <div className="bg-indigo-50 border-2 border-indigo-300 rounded-lg p-4" data-testid="inventory-flags-banner">
          <h4 className="text-sm font-bold text-indigo-800 mb-3">⚑ Needs Attention ({flags.length})</h4>
          <div className="space-y-2">
            {flags.map((f) => (
              <div key={f.id} className="bg-white border border-indigo-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-sm text-indigo-900">{f.message}</span>
                <button
                  data-testid={`resolve-flag-${f.id}`}
                  onClick={() => resolveFlag(f.id)}
                  className="text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded whitespace-nowrap"
                >
                  Mark done
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ORDER NOW Banner */}
      {orderNow.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4" data-testid="order-now-banner">
          <h4 className="text-sm font-bold text-red-800 mb-3">Order Now ({orderNow.length} items)</h4>
          <div className="space-y-2">
            {orderNow.map((a, i) => {
              const suggestion = reorderSuggestions.find(s => s.item_id === a.item_id);
              return (
                <div key={i} className="bg-white border border-red-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-red-900">{a.item_name}</span>
                      <span className="text-xs text-red-600 ml-2">({a.category})</span>
                    </div>
                    <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-1 rounded">
                      {a.days_remaining != null ? `${Math.round(a.days_remaining)} days left` : 'Below minimum'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                    <span>Stock: <strong>{a.current_stock} {a.unit_type}</strong> | Burn rate: {a.daily_consumption}/day</span>
                    {a.reorder_date && <span>Order by: <strong className="text-red-700">{new Date(a.reorder_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</strong></span>}
                  </div>
                  {suggestion && (
                    <div className="mt-2 text-xs bg-red-50 border border-red-100 rounded px-3 py-1.5 text-red-800">
                      Suggested: <strong>Order {suggestion.suggested_quantity} {suggestion.purchase_unit}</strong>
                      {suggestion.estimated_cost > 0 && <span> (~${suggestion.estimated_cost.toFixed(2)})</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ORDER SOON Section */}
      {orderSoon.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="order-soon-banner">
          <h4 className="text-sm font-semibold text-amber-800 mb-3">Order Soon ({orderSoon.length} items)</h4>
          <div className="space-y-2">
            {orderSoon.map((a, i) => {
              const suggestion = reorderSuggestions.find(s => s.item_id === a.item_id);
              return (
                <div key={i} className="bg-white border border-amber-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-amber-900">{a.item_name}</span>
                      <span className="text-xs text-amber-600 ml-2">({a.category})</span>
                    </div>
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded">
                      {a.days_remaining != null ? `~${Math.round(a.days_remaining)} days left` : 'Getting low'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                    <span>Stock: <strong>{a.current_stock} {a.unit_type}</strong> | Burn rate: {a.daily_consumption}/day</span>
                    {a.reorder_date && <span>Order by: <strong className="text-amber-700">{new Date(a.reorder_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</strong></span>}
                  </div>
                  {suggestion && (
                    <div className="mt-2 text-xs bg-amber-50 border border-amber-100 rounded px-3 py-1.5 text-amber-800">
                      Suggested: <strong>Order {suggestion.suggested_quantity} {suggestion.purchase_unit}</strong>
                      {suggestion.estimated_cost > 0 && <span> (~${suggestion.estimated_cost.toFixed(2)})</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Good */}
      {alerts.length === 0 && Object.keys(categories).length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center" data-testid="all-good-banner">
          <p className="text-sm font-medium text-green-800">All stock levels healthy</p>
          <p className="text-xs text-green-600 mt-1">No items need reordering right now</p>
        </div>
      )}

      {/* Category Cards */}
      {Object.keys(categories).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium">No inventory items yet</p>
          <p className="text-sm mt-1">Switch to the Stock Items tab to add your first item.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(categories).map(([cat, items]) => {
            const criticalCount = items.filter(i => i.status === 'critical').length;
            const warningCount = items.filter(i => i.status === 'warning').length;
            const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
            return (
              <div key={cat} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`category-card-${cat}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-800">{catLabel}</h4>
                  <span className="text-xs text-gray-500">{items.length} items</span>
                </div>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${STATUS_DOT[item.status] || STATUS_DOT.unknown}`} />
                        <span className="text-gray-700">{item.name}</span>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <span className="font-medium text-gray-900">{item.current_stock} {item.unit_type}</span>
                        {item.days_remaining != null && (
                          <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[item.status]}`}>
                            {Math.round(item.days_remaining)}d
                          </span>
                        )}
                        {item.reorder_date && item.status !== 'good' && (
                          <span className="text-gray-400">by {new Date(item.reorder_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {(criticalCount > 0 || warningCount > 0) && (
                  <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
                    {criticalCount > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">{criticalCount} critical</span>}
                    {warningCount > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{warningCount} low</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// STOCK ITEMS TAB
// ============================================================================
const StockItemsTab = ({ items, suppliers, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustItem, setAdjustItem] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('purchase');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [collapsedCats, setCollapsedCats] = useState(() => {
    const initial = {};
    CATEGORIES.forEach(c => { initial[c.value] = true; });
    return initial;
  });
  const [form, setForm] = useState({
    name: '', category: 'urns', unit_type: 'individual', current_stock: 0,
    minimum_stock: 0, warehouse_location: '', purchase_unit: '', purchase_unit_qty: 1,
    cost_per_unit: 0, supplier_id: '', lead_time_days: 7
  });

  const toggleCategory = (cat) => setCollapsedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  // Group items by category
  const grouped = {};
  items.forEach(item => {
    const cat = item.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  });

  const resetForm = () => {
    setForm({ name: '', category: 'urns', unit_type: 'individual', current_stock: 0, minimum_stock: 0, warehouse_location: '', purchase_unit: '', purchase_unit_qty: 1, cost_per_unit: 0, supplier_id: '', lead_time_days: 7 });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { showToast('Name is required', 'error'); return; }
    try {
      if (editingItem) {
        await apiClient.put(`/inventory/items/${editingItem.id}`, form);
        showToast('Item updated');
      } else {
        await apiClient.post('/inventory/items', form);
        showToast('Item created');
      }
      resetForm();
      onRefresh();
    } catch (e) { showToast('Failed to save item', 'error'); }
  };

  const handleAdjust = async () => {
    if (!adjustQty || parseFloat(adjustQty) === 0) { showToast('Enter a quantity', 'error'); return; }
    try {
      await apiClient.post('/inventory/adjust', {
        item_id: adjustItem.id, quantity_change: parseFloat(adjustQty),
        reason: adjustReason, notes: adjustNotes
      });
      showToast(`Stock adjusted: ${parseFloat(adjustQty) > 0 ? '+' : ''}${adjustQty} ${adjustItem.unit_type}`);
      setAdjustItem(null);
      setAdjustQty('');
      setAdjustNotes('');
      onRefresh();
    } catch (e) { showToast(e.response?.data?.detail || 'Failed to adjust', 'error'); }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = async (item) => {
    if (confirmDelete !== item.id) {
      setConfirmDelete(item.id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await apiClient.delete(`/inventory/items/${item.id}`);
      showToast('Item removed');
      setConfirmDelete(null);
      onRefresh();
    } catch (e) { showToast('Failed to delete', 'error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">{items.length} items across {Object.keys(grouped).length} categories</span>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700" data-testid="add-inventory-item-btn">+ Add Item</button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="inventory-item-form">
          <h4 className="text-sm font-semibold text-gray-800">{editingItem ? 'Edit Item' : 'New Item'}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" placeholder="e.g. Small Ceramic Urn" data-testid="item-name-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Unit Type</label>
              <select value={form.unit_type} onChange={e => setForm({...form, unit_type: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded">
                {UNIT_TYPES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Current Stock</label>
              <input type="number" step="0.1" value={form.current_stock} onChange={e => setForm({...form, current_stock: parseFloat(e.target.value) || 0})} className="w-full text-sm px-2 py-1.5 border rounded" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Minimum Stock</label>
              <input type="number" step="0.1" value={form.minimum_stock} onChange={e => setForm({...form, minimum_stock: parseFloat(e.target.value) || 0})} className="w-full text-sm px-2 py-1.5 border rounded" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Warehouse Location</label>
              <input value={form.warehouse_location} onChange={e => setForm({...form, warehouse_location: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" placeholder="e.g. Shelf A3" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Purchase Unit</label>
              <input value={form.purchase_unit} onChange={e => setForm({...form, purchase_unit: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" placeholder="e.g. box of 50" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Qty per Purchase</label>
              <input type="number" step="1" value={form.purchase_unit_qty} onChange={e => setForm({...form, purchase_unit_qty: parseFloat(e.target.value) || 1})} className="w-full text-sm px-2 py-1.5 border rounded" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Cost per Unit ($)</label>
              <input type="number" step="0.01" value={form.cost_per_unit} onChange={e => setForm({...form, cost_per_unit: parseFloat(e.target.value) || 0})} className="w-full text-sm px-2 py-1.5 border rounded" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Supplier</label>
              <select value={form.supplier_id} onChange={e => setForm({...form, supplier_id: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded">
                <option value="">-- None --</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lead Time (days)</label>
              <input type="number" value={form.lead_time_days} onChange={e => setForm({...form, lead_time_days: parseInt(e.target.value) || 7})} className="w-full text-sm px-2 py-1.5 border rounded" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} className="px-4 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700" data-testid="save-item-btn">{editingItem ? 'Update' : 'Create'}</button>
            <button onClick={resetForm} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      {/* Adjust Stock Modal */}
      {adjustItem && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="adjust-stock-form">
          <h4 className="text-sm font-semibold text-gray-800">Adjust Stock: {adjustItem.name} (current: {adjustItem.current_stock} {adjustItem.unit_type})</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Quantity (+/-)</label>
              <input type="number" step="0.1" value={adjustQty} onChange={e => setAdjustQty(e.target.value)} className="w-full text-sm px-2 py-1.5 border rounded" placeholder="+50 or -3" data-testid="adjust-qty-input" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Reason</label>
              <select value={adjustReason} onChange={e => setAdjustReason(e.target.value)} className="w-full text-sm px-2 py-1.5 border rounded">
                <option value="purchase">Purchase Received</option>
                <option value="manual_adjustment">Manual Adjustment</option>
                <option value="correction">Correction</option>
                <option value="damaged">Damaged/Waste</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Notes</label>
              <input value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)} className="w-full text-sm px-2 py-1.5 border rounded" placeholder="Optional notes..." />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdjust} className="px-4 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700" data-testid="confirm-adjust-btn">Adjust Stock</button>
            <button onClick={() => setAdjustItem(null)} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      {/* Collapsible Category Sections */}
      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No items yet. Click "+ Add Item" to get started.</div>
      ) : (
        <div className="space-y-3" data-testid="inventory-items-grouped">
          {CATEGORIES.filter(c => grouped[c.value]?.length > 0).map(cat => {
            const catItems = grouped[cat.value] || [];
            const isCollapsed = collapsedCats[cat.value];
            const critCount = catItems.filter(i => i.status === 'critical').length;
            const warnCount = catItems.filter(i => i.status === 'warning').length;
            
            return (
              <div key={cat.value} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`stock-category-${cat.value}`}>
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(cat.value)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  data-testid={`toggle-${cat.value}`}
                >
                  <div className="flex items-center gap-3">
                    <svg className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-800">{cat.label}</span>
                    <span className="text-xs text-gray-500">({catItems.length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {critCount > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">{critCount} critical</span>}
                    {warnCount > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">{warnCount} low</span>}
                    {critCount === 0 && warnCount === 0 && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">All good</span>}
                  </div>
                </button>

                {/* Category Items */}
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-white border-b border-t">
                          <th className="text-left px-3 py-1.5 font-medium text-gray-500 w-6"></th>
                          <th className="text-left px-3 py-1.5 font-medium text-gray-500">Name</th>
                          <th className="text-left px-3 py-1.5 font-medium text-gray-500 hidden md:table-cell">Location</th>
                          <th className="text-right px-3 py-1.5 font-medium text-gray-500">Stock</th>
                          <th className="text-right px-3 py-1.5 font-medium text-gray-500 hidden md:table-cell">Min</th>
                          <th className="text-right px-3 py-1.5 font-medium text-gray-500 hidden md:table-cell">Burn/Day</th>
                          <th className="text-right px-3 py-1.5 font-medium text-gray-500">Days Left</th>
                          <th className="text-right px-3 py-1.5 font-medium text-gray-500 hidden md:table-cell">Order By</th>
                          <th className="text-center px-3 py-1.5 font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catItems.map(item => (
                          <tr key={item.id} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2"><div className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[item.status] || STATUS_DOT.unknown}`} /></td>
                            <td className="px-3 py-2 font-medium text-gray-900">{item.name}</td>
                            <td className="px-3 py-2 text-gray-500 hidden md:table-cell">{item.warehouse_location || '-'}</td>
                            <td className="px-3 py-2 text-right font-medium">{item.current_stock} {item.unit_type}</td>
                            <td className="px-3 py-2 text-right text-gray-500 hidden md:table-cell">{item.minimum_stock}</td>
                            <td className="px-3 py-2 text-right text-gray-500 hidden md:table-cell">{item.daily_consumption || 0}</td>
                            <td className="px-3 py-2 text-right">
                              {item.days_remaining != null ? (
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[item.status]}`}>
                                  {Math.round(item.days_remaining)}d
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-500 hidden md:table-cell">
                              {item.reorder_date ? (
                                <span className={item.status !== 'good' ? 'font-medium text-red-600' : ''}>
                                  {new Date(item.reorder_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center gap-1">
                                <button onClick={() => setAdjustItem(item)} className="px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 text-xs" title="Adjust stock">+/-</button>
                                <button onClick={() => { setForm({...item, supplier_id: item.supplier_id || ''}); setEditingItem(item); setShowForm(true); }} className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs" title="Edit">Edit</button>
                                <button onClick={() => handleDelete(item)} className={`px-2 py-1 rounded text-xs ${confirmDelete === item.id ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'}`} title="Delete">{confirmDelete === item.id ? 'Sure?' : 'X'}</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// USAGE TRACKING TAB
// ============================================================================
const UsageTrackingTab = ({ movements, loading }) => {
  const reasonLabels = { auto: 'Auto (Pet)', pet_record: 'Auto (Pet)', cycle: 'Auto (Cycle)', purchase: 'Purchase', manual_adjustment: 'Manual', correction: 'Correction', damaged: 'Damaged' };
  const reasonColors = { auto: 'bg-blue-100 text-blue-700', pet_record: 'bg-blue-100 text-blue-700', cycle: 'bg-purple-100 text-purple-700', purchase: 'bg-green-100 text-green-700', manual_adjustment: 'bg-gray-100 text-gray-700', correction: 'bg-amber-100 text-amber-700', damaged: 'bg-red-100 text-red-700' };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading movements...</div>;

  return (
    <div className="space-y-4">
      <span className="text-sm text-gray-600">{movements.length} recent movements</span>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="movements-table">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-3 py-2 font-medium text-gray-600">Date</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Item</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Change</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">After</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Reason</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600 hidden md:table-cell">Notes</th>
            </tr>
          </thead>
          <tbody>
            {movements.map(m => (
              <tr key={m.id} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                  {new Date(m.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                  <span className="text-gray-400 ml-1">{new Date(m.created_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                </td>
                <td className="px-3 py-2 font-medium text-gray-900">{m.item_name}</td>
                <td className={`px-3 py-2 text-right font-medium ${m.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {m.quantity_change > 0 ? '+' : ''}{m.quantity_change}
                </td>
                <td className="px-3 py-2 text-right text-gray-600">{m.stock_after}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${reasonColors[m.reason] || 'bg-gray-100 text-gray-600'}`}>
                    {reasonLabels[m.reason] || m.reason}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-500 hidden md:table-cell max-w-xs truncate">{m.notes || '-'}</td>
              </tr>
            ))}
            {movements.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500">No stock movements yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// SUPPLIERS & RESTOCK TAB
// ============================================================================
const SuppliersTab = ({ suppliers, purchaseOrders, reorderSuggestions, onRefresh }) => {
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ name: '', contact_email: '', contact_phone: '', notes: '' });
  const [showPOForm, setShowPOForm] = useState(false);

  const handleSaveSupplier = async () => {
    if (!supplierForm.name.trim()) { showToast('Name required', 'error'); return; }
    try {
      await apiClient.post('/inventory/suppliers', supplierForm);
      showToast('Supplier created');
      setShowSupplierForm(false);
      setSupplierForm({ name: '', contact_email: '', contact_phone: '', notes: '' });
      onRefresh();
    } catch (e) { showToast('Failed to save', 'error'); }
  };

  const handleDeleteSupplier = async (sup) => {
    if (!window.confirm(`Remove "${sup.name}"?`)) return;
    try {
      await apiClient.delete(`/inventory/suppliers/${sup.id}`);
      showToast('Supplier removed');
      onRefresh();
    } catch (e) { showToast('Failed', 'error'); }
  };

  const handlePOStatus = async (orderId, newStatus) => {
    try {
      await apiClient.put(`/inventory/purchase-orders/${orderId}/status`, { status: newStatus });
      showToast(`PO marked as ${newStatus}`);
      onRefresh();
    } catch (e) { showToast('Failed to update PO', 'error'); }
  };

  const handleGenerateReorder = async () => {
    try {
      const res = await apiClient.post('/inventory/generate-reorder');
      if (res.data.suggestions?.length > 0) {
        showToast(`${res.data.count} items need reordering`);
      } else {
        showToast('All stock levels are healthy');
      }
      onRefresh();
    } catch (e) { showToast('Failed to generate', 'error'); }
  };

  return (
    <div className="space-y-6">
      {/* Suppliers Section */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold text-gray-800">Suppliers</h4>
          <button onClick={() => setShowSupplierForm(true)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700" data-testid="add-supplier-btn">+ Add Supplier</button>
        </div>

        {showSupplierForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className="block text-xs text-gray-600 mb-1">Name *</label><input value={supplierForm.name} onChange={e => setSupplierForm({...supplierForm, name: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" data-testid="supplier-name-input" /></div>
              <div><label className="block text-xs text-gray-600 mb-1">Email</label><input value={supplierForm.contact_email} onChange={e => setSupplierForm({...supplierForm, contact_email: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" /></div>
              <div><label className="block text-xs text-gray-600 mb-1">Phone</label><input value={supplierForm.contact_phone} onChange={e => setSupplierForm({...supplierForm, contact_phone: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" /></div>
              <div><label className="block text-xs text-gray-600 mb-1">Notes</label><input value={supplierForm.notes} onChange={e => setSupplierForm({...supplierForm, notes: e.target.value})} className="w-full text-sm px-2 py-1.5 border rounded" /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveSupplier} className="px-4 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">Save</button>
              <button onClick={() => setShowSupplierForm(false)} className="px-4 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {suppliers.map(s => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-3 flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-900">{s.name}</p>
                {s.contact_email && <p className="text-xs text-gray-500">{s.contact_email}</p>}
                {s.contact_phone && <p className="text-xs text-gray-500">{s.contact_phone}</p>}
                {s.notes && <p className="text-xs text-gray-400 mt-1">{s.notes}</p>}
              </div>
              <button onClick={() => handleDeleteSupplier(s)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
            </div>
          ))}
          {suppliers.length === 0 && <p className="text-sm text-gray-500 col-span-2">No suppliers yet.</p>}
        </div>
      </div>

      {/* Reorder Suggestions */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold text-gray-800">Reorder Suggestions</h4>
          <button onClick={handleGenerateReorder} className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700" data-testid="generate-reorder-btn">Check Reorder Needs</button>
        </div>
        {reorderSuggestions.length > 0 ? (
          <div className="space-y-2">
            {reorderSuggestions.map((s, i) => (
              <div key={i} className={`flex items-center justify-between text-xs px-3 py-2 rounded border ${STATUS_COLORS[s.urgency]}`}>
                <div>
                  <span className="font-medium">{s.item_name}</span>
                  <span className="text-gray-500 ml-2">({s.current_stock} left, {s.days_remaining != null ? `${Math.round(s.days_remaining)} days` : 'below min'})</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">Order {s.suggested_quantity} {s.purchase_unit}</span>
                  {s.estimated_cost > 0 && <span className="text-gray-500 ml-1">(~${s.estimated_cost.toFixed(2)})</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No items need reordering right now.</p>
        )}
      </div>

      {/* Purchase Orders */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Purchase Orders</h4>
        {purchaseOrders.length > 0 ? (
          <div className="space-y-2">
            {purchaseOrders.map(po => (
              <div key={po.id} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`po-${po.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{po.supplier_name || 'Unknown Supplier'}</p>
                    <p className="text-xs text-gray-500">
                      {po.items?.length || 0} items - ${po.total_cost?.toFixed(2) || '0.00'}
                      <span className="ml-2">{new Date(po.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      po.status === 'received' ? 'bg-green-100 text-green-700' :
                      po.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                      po.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{po.status}</span>
                    {po.status === 'pending' && (
                      <button onClick={() => handlePOStatus(po.id, 'ordered')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">Mark Ordered</button>
                    )}
                    {po.status === 'ordered' && (
                      <button onClick={() => handlePOStatus(po.id, 'received')} className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">Mark Received</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No purchase orders yet.</p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STOCKTAKE TAB — quick bulk count to set real stock levels
// ============================================================================
const StocktakeTab = ({ items, onRefresh }) => {
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);

  const grouped = {};
  items.forEach(i => {
    const cat = i.category || 'other';
    (grouped[cat] = grouped[cat] || []).push(i);
  });

  const changedEntries = items.filter(i =>
    counts[i.id] !== undefined && counts[i.id] !== '' && parseFloat(counts[i.id]) !== i.current_stock
  );

  const submit = async () => {
    if (!changedEntries.length) return;
    setSaving(true);
    try {
      await apiClient.post('/inventory/stocktake', {
        entries: changedEntries.map(i => ({ item_id: i.id, counted_qty: parseFloat(counts[i.id]) })),
        notes: 'Stocktake via CRM'
      });
      showToast(`Stocktake saved — ${changedEntries.length} item(s) updated`, 'success');
      setCounts({});
      onRefresh();
    } catch (e) {
      showToast('Stocktake failed to save', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="stocktake-tab-content">
      <div className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
        <div className="text-sm text-blue-900">
          <strong>Stocktake mode</strong> — type the real counted quantity next to any item. Only changed items are saved.
          {changedEntries.length > 0 && <span className="ml-2 font-bold">({changedEntries.length} change{changedEntries.length > 1 ? 's' : ''} pending)</span>}
        </div>
        <button
          data-testid="stocktake-submit-btn"
          onClick={submit}
          disabled={saving || changedEntries.length === 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap"
        >
          {saving ? 'Saving…' : `Save Counts${changedEntries.length ? ` (${changedEntries.length})` : ''}`}
        </button>
      </div>

      {CATEGORIES.filter(c => grouped[c.value]?.length).map(cat => (
        <div key={cat.value} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`stocktake-category-${cat.value}`}>
          <div className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">{cat.label} ({grouped[cat.value].length})</div>
          <table className="w-full text-sm">
            <tbody>
              {grouped[cat.value].sort((a, b) => a.name.localeCompare(b.name)).map(item => {
                const val = counts[item.id];
                const hasChange = val !== undefined && val !== '' && parseFloat(val) !== item.current_stock;
                const diff = hasChange ? parseFloat(val) - item.current_stock : 0;
                return (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-800">{item.name}</td>
                    <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                      now: <strong className="text-gray-700">{item.current_stock}</strong> {item.unit_type === 'kg' ? 'kg' : ''}
                    </td>
                    <td className="px-3 py-2 w-32">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        data-testid={`stocktake-input-${item.id}`}
                        placeholder="count"
                        value={val ?? ''}
                        onChange={e => setCounts({ ...counts, [item.id]: e.target.value })}
                        className={`w-full text-sm px-2 py-1.5 border rounded text-right ${hasChange ? 'border-blue-400 bg-blue-50 font-semibold' : 'border-gray-200'}`}
                      />
                    </td>
                    <td className="px-3 py-2 w-20 text-right">
                      {hasChange && (
                        <span className={`text-xs font-bold ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {diff > 0 ? '+' : ''}{Math.round(diff * 1000) / 1000}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN INVENTORY MODAL
// ============================================================================
const InventoryModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [reorderSuggestions, setReorderSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, itemsRes, movementsRes, suppliersRes, posRes] = await Promise.all([
        apiClient.get('/inventory/overview'),
        apiClient.get('/inventory/items'),
        apiClient.get('/inventory/movements?limit=200'),
        apiClient.get('/inventory/suppliers'),
        apiClient.get('/inventory/purchase-orders')
      ]);
      setOverview(overviewRes.data);
      setItems(itemsRes.data.items || []);
      setMovements(movementsRes.data.movements || []);
      setSuppliers(suppliersRes.data.suppliers || []);
      setPurchaseOrders(posRes.data.orders || []);
    } catch (e) {
      console.error('Failed to fetch inventory data:', e);
    }
    setLoading(false);
  }, []);

  const fetchReorder = useCallback(async () => {
    try {
      const res = await apiClient.post('/inventory/generate-reorder');
      setReorderSuggestions(res.data.suggestions || []);
    } catch (e) { console.error('Failed to fetch reorder suggestions:', e); }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAll();
      fetchReorder();
    }
  }, [isOpen, fetchAll, fetchReorder]);

  const handleRefresh = () => { fetchAll(); fetchReorder(); };

  if (!isOpen) return null;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'stock', label: 'Stock Items' },
    { key: 'stocktake', label: 'Stocktake' },
    { key: 'usage', label: 'Usage Tracking' },
    { key: 'suppliers', label: 'Suppliers & Restock' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50" data-testid="inventory-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 md:px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900" data-testid="inventory-modal-title">Inventory Management</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="inventory-modal-close">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-4 md:px-6 flex-shrink-0 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`inventory-tab-${tab.key}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.key === 'overview' && overview?.alerts?.length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{overview.alerts.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'overview' && <OverviewTab overview={overview} loading={loading} reorderSuggestions={reorderSuggestions} onRefresh={handleRefresh} />}
          {activeTab === 'stock' && <StockItemsTab items={items} suppliers={suppliers} onRefresh={handleRefresh} />}
          {activeTab === 'stocktake' && <StocktakeTab items={items} onRefresh={handleRefresh} />}
          {activeTab === 'usage' && <UsageTrackingTab movements={movements} loading={loading} />}
          {activeTab === 'suppliers' && <SuppliersTab suppliers={suppliers} purchaseOrders={purchaseOrders} reorderSuggestions={reorderSuggestions} onRefresh={handleRefresh} />}
        </div>
      </div>
    </div>
  );
};

export default InventoryModal;
